/**
 * `@mhosaic/feedback/loader` — public entry for the loader architecture.
 *
 * Same shape as the direct-import path (`@mhosaic/feedback`), but the
 * widget bundle is fetched at runtime from a CDN URL the Mhosaic backend
 * dictates via the manifest endpoint. Letting hosts switch from the
 * direct-import path to the loader path gives them auto-updates without
 * any further code changes — Mhosaic ships a new Release row, every host
 * picks up the new bundle on next page load.
 *
 * Public surface MUST mirror the direct-import path exactly:
 *   - `createFeedback(config)` returns a `FeedbackApi`-shaped handle.
 *   - Method calls before the bundle finishes loading queue up and replay.
 *   - `submit()` returns a Promise that resolves after the bundle is up
 *     and the real submit completes.
 *
 * If the manifest endpoint reports the widget is disabled for this
 * project (`enabled: false` — kill switch or no stable release configured),
 * the deferred handle silently no-ops void calls and rejects `submit()`
 * calls with a clear error. The host page never crashes.
 */

import type { FeedbackApi, FeedbackConfig } from '../types'
import type { ErrorTrackingOptions } from '../modules/error-tracking'
import { injectBundle } from './bundleLoader'
import { fetchManifest } from './manifest'
import { createDeferredApi, type DeferredHandle } from './deferredApi'

export type { FeedbackApi, FeedbackConfig } from '../types'
export type { Manifest } from './manifest'

/** Loader config = the public widget config plus the loader-only knobs the
 *  host can set to override what the manifest dictates. */
export type LoaderConfig = FeedbackConfig & {
  /** Override the bundle's default error capture. Omit (or pass `undefined`)
   *  to let the project's manifest flag decide (which itself defaults on). */
  errorTracking?: boolean | ErrorTrackingOptions | undefined
}

export function createFeedback(config: LoaderConfig): FeedbackApi {
  const deferred = createDeferredApi()
  loadAndAttach(config, deferred).catch((err: Error) => {
    // Surface to console — host devtools is the canonical channel for
    // widget bootstrap errors. Production telemetry is the bundle's job
    // (it can't run if it never loaded), so console.warn is the floor.
    // eslint-disable-next-line no-console
    console.warn('[mhosaic-feedback] widget did not load:', err.message)
    deferred._fail(err)
  })
  return deferred
}

async function loadAndAttach(
  config: LoaderConfig,
  deferred: DeferredHandle,
): Promise<void> {
  const manifest = await fetchManifest(config.endpoint, config.apiKey)
  if (!manifest.enabled || !manifest.bundle_url || !manifest.sri_hash) {
    // Disabled by Mhosaic (kill switch) or no stable release. Surface a
    // clear failure so submit() callers see an error, but no host crash.
    deferred._fail(
      new Error(
        `mhosaic-feedback: widget disabled for this project${
          manifest.detail ? ` — ${manifest.detail}` : ''
        }`,
      ),
    )
    return
  }
  const bundle = await injectBundle({
    bundleUrl: manifest.bundle_url,
    sriHash: manifest.sri_hash,
  })
  const real: FeedbackApi = bundle.createFeedback({
    ...config,
    // Forward the project's per-end-user gating flag from the manifest unless
    // the host set it explicitly. The widget then runs the visibility check.
    requiresVisibilityCheck:
      config.requiresVisibilityCheck ??
      manifest.config?.requires_visibility_check ??
      false,
    // Error capture is armed INSIDE the bundle (the only auto-updating part
    // of a loader install), so already-deployed hosts pick it up without a
    // redeploy. Precedence: explicit host override > per-project manifest
    // flag > on. The loader provider forwards its `errorTracking` prop here
    // rather than wrapping host-side, so there's exactly one arm point (the
    // real instance) — no deferred/real double-registration.
    errorTracking:
      config.errorTracking ?? manifest.config?.error_tracking ?? true,
  })
  deferred._attach(real)
}
